import { DepInjMixin, requires } from '@APP/mixins/dependency';
import { InsuranceService } from '@APP/services/insurance-service';
import { QuestionsService } from '@APP/services/questions-service';
import { t } from '@APP/utils/locale';
import { css, html, nothing } from 'lit';
import { customElement, queryAll } from 'lit/decorators';
import { commonPopupStyle } from '../../styles/common-style';
import { DeviceAwareComponent } from '../components/device-aware-component';
import { OpenIcon, FaqIcon } from '../icons/icons';

type deps = {
  insuranceService: InsuranceService;
  questionsService: QuestionsService;
};

const faqMap = [
  { id: 1, iconSvg: FaqIcon, title: 'ACH', text: 'Automated Clearing House - a bank payment.' },
  { id: 2, iconSvg: FaqIcon, title: 'Electronic Funds Transfer(EFT)', text: 'An electronic funds transfer (EFT) pays your bill automatically by transferring money from your credit card, checking account, or savings account directly to your insurance company each month.' },
  { id: 3, iconSvg: FaqIcon, title: t('Q_BI_Bodily_Injury_Liability_YN'), text: 'Covers injury you might cause someone while using your boat; includes medical bills, lost income, pain and suffering, and legal expenses. Coverage is subject to policy limits described on your declaration page and within the policy document itself.' },
  { id: 4, iconSvg: FaqIcon, title: t('Q_PD_Property_Damage_Liability_YN'), text: 'Covers damage you might cause to someone else’s boat, dock, or other property or structures. Coverage is subject to policy limits described on your declaration page and within the policy document itself.' },
  { id: 5, iconSvg: FaqIcon, title: t('Q_MP_Medical_Payments_YN'), text: 'Medical payments is coverage for people who are insured while on, boarding, leaving, your boat or involved in an accident with another boat. Regardless of who is at fault. Coverage is subject to policy limits described on your declaration page and within the policy document itself.' },
  { id: 6, iconSvg: FaqIcon, title: t('Q_UBUIBBI_Uninsured_Underinsured_Boater_YN'), text: 'What would happen if an uninsured boater collides with your watercraft and you or one of your passengers are injured? The uninsured watercraft coverage on your boat insurance policy may help pay for the resulting expenses. Coverage is subject to policy limits described on your declaration page and within the policy document itself.' },
  { id: 7, iconSvg: FaqIcon, title: t('Q_COLL_Coverage_Deductible_Amount'), text: 'Means any accident involving two or more vessels which causes loss or damage even if no actual contact has taken place. Includes repair or replacement of your vessel. Coverage is subject to policy limits described on your declaration page and within the policy document itself.' },
  { id: 8, iconSvg: FaqIcon, title: t('Q_COMP_Coverage_Deductible_Amount'), text: 'Provides compensation if your boat is vandalized, stolen, or damaged in an incident other than a collision. Coverage is subject to policy limits described on your declaration page and within the policy document itself.' },
  { id: 9, iconSvg: FaqIcon, title: 'Replacement Cost', text: 'Total Replacement Cost agrees to replace your boat with a new boat of similar make and model. This coverage can be purchased only when the boat is new and the coverage is only available until the boat is two years old.' },
  { id: 10, iconSvg: FaqIcon, title: 'Agreed value', text: 'Covers the boat based on its value when the policy was written. While it can cost more up front, there is no depreciation for a total loss of the boat (some partial losses may be depreciated).' },
  { id: 11, iconSvg: FaqIcon, title: 'Actual cash value', text: 'Costs less up front, but factors in depreciation. In other words, the policy will only pay up to the actual cash value of the boat at the time it is declared a total or partial loss.' },
  { id: 12, iconSvg: FaqIcon, title: 'Personal Watercraft (PWC)', text: "A PWC includes jet skis, wave runners and similar vessels that have an engine used for propulsion, fully enclosed hull, i.e. doesn't retain water and are operated by standing, kneeling or sitting astride." },
  { id: 13, iconSvg: FaqIcon, title: t('Q_PEREF_Personal_Effects_YN'), text: 'This covers personal belongings such as your camera, clothing, portable speakers, SCUBA gear, etc. Coverage is subject to policy limits described on your declaration page and within the policy document itself.' },
  { id: 14, iconSvg: FaqIcon, title: t('Q_FSHEQ_Fishing_Equipment_YN'), text: '"FISHING EQUIPMENT" means any equipment, owned by you or a family member, that is used for recreational fishing. Fishing equipment includes, but is not limited to, rods, reels, lures and tackle boxes.' },
  { id: 15, iconSvg: FaqIcon, title: t('Q_TL_On_Water_Towing_YN'), text: "With On Water Towing you'll never have to worry about on-water breakdowns again. If your boat is disabled on the water, we’ll reimburse you for on-water towing, jump starts, soft un-groundings and fuel delivery, up to your policy limit." },
  { id: 16, iconSvg: FaqIcon, title: 'Fuel Spill', text: 'Ahoy pays for fuel spills, sunken boat, leaking tank, or a mishap at the fueling station.' },

];


@requires(QuestionsService.serviceName, InsuranceService.serviceName)
@customElement('pricing-packages-faq-popup')
export class PricingPackagesFaqPopup extends DepInjMixin<deps>(DeviceAwareComponent) {
  static get desktopStyles() {
    return [];
  }
  static get mobileStyles() {
    return [css`
      .content{
        padding-block-start:unset;
        height:60vh;
      }
    .panel{
      padding: 0 8rem 0 1.25rem;
    }
  :host::-webkit-scrollbar {
    display: none;
    background: red;
  }`,
    ];
  };

  static get baseStyles() {
    return [commonPopupStyle, css`
      :host{
        --popup-overflow:auto;
      }

    h2 {
      color: #0D2D5F;
      font-size: 3rem;
      font-style: normal;
      font-weight: 700;
      line-height: 1.33;
      text-align: left;
      margin: 0;
      margin-block-end: 1.25rem;
    }

    h3 {
      color: rgba(18, 18, 19, 0.4);;
      font-size: 2rem;
      line-height: 1.5;
      font-style: normal;
      font-weight: 500;
      text-align: left;
      margin: 0;
      margin-block-end: 3rem;
    }
    
    .content {
      max-width: 102rem;
      max-height: 100rem;
      justify-content: center;
      padding: 0;
      background-color: #F5F6F9;
      border-radius: 1.25rem;
    }

    .single-button-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .icon-container {
      max-width: 6.25rem;
      max-height: 6.25rem;
      margin-inline-start: 1.25rem;
      margin-inline-end: 1.25rem;
    }

    .icon-container svg{
      max-width: 6.25rem;
      max-height: 6.25rem;
    }

    .open-icon-container {
      max-width: 1rem;
      max-height: 2rem;
      margin-inline-end: 2rem;
    }

    .divider-container {
      width: 100%;
    }

    .divider {
      border-width: 0;
      width: 100%;
      background: #DDDCE5;
      height: 0.125rem;
    }

    button.accordion {
      background-color: unset;
      color: #121213;
      cursor: pointer;
      padding: 2rem;
      padding-left: 0;
      width: 100%;
      border: none;
      outline: none;
      font-size: 2rem;
      line-height: 1.5;
      font-weight: 500;
      transition: 0.4s;
      text-align: left;
    }

    .active,
    .single-button-container:hover {
      border-radius: 1.25rem 1.25rem 0 0;
    }

    .panel {
      padding: 0 16.25rem 0 8.5rem;
      background-color: white;
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.2s ease-out;
      border-radius: 0 0 1.25rem 1.25rem;
    }

    .complete-details-header {
      margin-block-start: 6.25rem;
      margin-block-end: 2.75rem;
      font-size: 3rem;
      font-style: normal;
      font-weight: 700;
      line-height: 4rem;
      letter-spacing: 0;
      text-align: left;
    }

    p {
      color: rgba(18, 18, 19, 0.6);
      font-size: 2rem;
      line-height: 1.5;
      font-weight: 500;
      text-align: left;
    }

    .panel p {
      margin-block-end: 2.75rem;
      margin-block-start: 0;
    }

    .p-header {
      margin-block-start: 2.5rem;
      margin-block-end: 0;
      font-size: 2.5rem;
    }

    .p-header:first-of-type {
      margin-block-start: 0;
    }

    .p-text {
      margin-block-start: 0;
    }

    .active {
      background-color: white;
      border-radius: 1.25rem 1.25rem 0 0;
    }
  `];
  };

  @queryAll('.panel')
  private panelElements!: any[];

  @queryAll('.single-button-container')
  private accordionElements!: any[];

  connectedCallback() {
    super.connectedCallback();
  }

  getAccordionData(accordionIndex: string) {
    const index = parseInt(accordionIndex);
    return faqMap.find((el) => el.id === index);
  }

  handleClick(e: MouseEvent) {
    if (!e) return;
    const target = e.currentTarget as HTMLElement;
    const accordionIndex = target.dataset.index || '';
    const accordionData = this.getAccordionData(accordionIndex);
    const panelId = accordionData!.id;
    const accordion = panelId ? this.accordionElements[panelId - 1] : nothing;
    const isActive = accordion.classList.contains('active');
    this.accordionElements.forEach(e => e.classList.remove('active'));
    this.panelElements.forEach(e => e.style.maxHeight = null);
    if (!isActive) {
      const panel = panelId ? this.panelElements[panelId - 1] : nothing;
      accordion.classList.add('active');
      panel.style.maxHeight = panel.scrollHeight + 'px';
    }

  }

  renderLayout() {
    const isMobile = this.userDevice === 'mobile';

    return html`
 <div class="content">
    <h2>${t('Faq_Text')}</h2>
    <h3>${t('Faq_Text2')}</h3>

      <div class="buttons-container">
      ${faqMap.map((el, index) => {
    const rowIndex = (index + 1);
    const icon = isMobile ? nothing : html`<div class="icon-container">${el.iconSvg}</div>`;
    return html`
        <div class="single-button-container"
        @click=${this.handleClick}
        data-index=${rowIndex}>
          ${icon}
          <button class="accordion">${t(el.title)}</button>
          <div class="open-icon-container">
            ${OpenIcon}
          </div>
        </div>
        <div class="panel">
          <p>${t(el.text)}</p>
        </div>
        <div class="divider-container">
          <hr class="divider">
        </div>`;
  })}
  </div>
    `;
  }

  renderContent() {
    return this.renderLayout();
  }
}
